/**
 * THE RECORDING LINE, HANDED FROM THE BAR TO THE NOTE (#14384).
 *
 * Stop settles the recording bar's waveform to a flat line and opens the
 * note, whose Gather motif starts on a flat line of its own. The two carry
 * one `<ViewTransition name>`, and a shared-element pair only morphs when
 * one side unmounts and the other mounts IN THE SAME COMMIT.
 *
 * The navigation cannot promise that commit. The root `loading.tsx` covers
 * `/notes/[id]`, so a push the router has nothing cached for commits that
 * skeleton first and the note a commit (or a second) later. Had the bar let
 * go of its line with the push, it would be gone before the motif arrived.
 *
 * So the bar KEEPS the line after the push and offers it here. The motif,
 * when it mounts, claims it inside one `startTransition` that both runs the
 * bar's release and mounts the motif's own line: one commit, one morph,
 * whatever the route passed through on the way. A motif that mounts on a
 * later stage (no start line) still claims, only to free the bar. With no
 * motif at all the bar withdraws after `RECORDING_LINE_HANDOFF_WAIT_MS` and
 * lets go on its own.
 *
 * Module-level, like the recording session store: the bar lives in the root
 * layout and the motif deep in a page, with no common parent to own this.
 * One offer at a time — a newer Stop replaces an older offer, and an older
 * offer's withdraw leaves the newer one alone.
 *
 * The offer is KEYED TO THE NOTE Stop opened. Only that note's motif may
 * claim it: a motif for any other note (a list, a second tab of cards, a
 * note reached some other way) neither claims nor waits for it, so it cannot
 * take the bar's line away from the note it was headed for.
 *
 * Pure: no React, no DOM.
 */

/**
 * How long the bar holds an unclaimed line after the push before letting go
 * on its own: long enough for a note to load, short enough that "Saved" does
 * not linger over a note with no processing card to hand it to.
 */
export const RECORDING_LINE_HANDOFF_WAIT_MS = 2_000

type Offer = { noteId: string; release: () => void }

let offer: Offer | null = null

/**
 * Offer the bar's line to the note `noteId`. Returns a withdraw that only
 * ever removes THIS offer.
 */
export function offerRecordingLine(
  noteId: string,
  release: () => void,
): () => void {
  const mine: Offer = { noteId, release }
  offer = mine
  return () => {
    if (offer === mine) offer = null
  }
}

/**
 * Is a line waiting to be claimed by the motif for `noteId`? Read by a motif
 * as it first renders.
 */
export function hasRecordingLineOffer(noteId: string | null): boolean {
  return noteId !== null && offer !== null && offer.noteId === noteId
}

/**
 * Take the line offered to `noteId`: runs the bar's release, once. Call it
 * inside the `startTransition` that mounts the claiming line, so both commit
 * together. Returns whether there was a line for this note to take; an offer
 * to another note is left where it is.
 */
export function claimRecordingLine(noteId: string | null): boolean {
  const taken = offer
  if (taken === null || noteId === null || taken.noteId !== noteId) {
    return false
  }
  offer = null
  taken.release()
  return true
}

/** Tests only: module state outlives a test file's renders. */
export function resetRecordingLineHandoff(): void {
  offer = null
}
